/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {ThunderDispatcher, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {_keys, generateHex, Module} from "@intuitionrobotics/ts-common";
import {DB_UserRun} from "@app/app-shared/cohort";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import {
    ApiGetUserRuns,
    ApiRemoveUserRuns,
    ApiSetUserRuns
} from "@app/app-shared/api";
import {Clause_Where} from "@intuitionrobotics/firebase";

export interface OnUserRunsUpdated {
    __onUserRunsUpdated: (id?: string) => void;
}

export const dispatch_onUserRunsUpdated = new ThunderDispatcher<OnUserRunsUpdated, "__onUserRunsUpdated">("__onUserRunsUpdated");

export class PatientCohortModule_Class
    extends Module {

    private userRuns?: DB_UserRun[];

    constructor() {
        super("PatientCohortModule");
    }

    fetchUserRuns = (patientId: string) => {
        XhrHttpModule
            .createRequest<ApiGetUserRuns>(HttpMethod.GET, "get-user-runs")
            .setRelativeUrl("/v1/cohort/get-user-runs")
            .setUrlParams({patientId})
            .setOnError("Failed to retrieve user runs")
            .execute(this.onRunsUpdate);
    };

    setUserRuns = (userRuns: DB_UserRun[]) => {
        if (!userRuns.length)
            return;

        const id = generateHex(8);
        XhrHttpModule
            .createRequest<ApiSetUserRuns>(HttpMethod.POST, "set-user-runs", id)
            .setRelativeUrl("/v1/cohort/set-user-runs")
            .setJsonBody(userRuns)
            .setOnError("Failed to set user runs")
            .execute(this.onRunsUpdate);

        return id;
    };

    onRunsUpdate = async (response: DB_UserRun[], id?: string): Promise<void> => {
        this.userRuns = this.userRuns || [];
        response.reduce((carry, el) => this.upsertRun(carry, el), this.userRuns);
        dispatch_onUserRunsUpdated.dispatchUI(id);
    };

    private upsertRun = (userRuns: DB_UserRun[], userRun: DB_UserRun) => {
        const itemIdx = userRuns.findIndex(_item => (
            _item.patientId === userRun.patientId &&
            _item.date === userRun.date &&
            _item.careElementKey === userRun.careElementKey
        ));
        const idx = itemIdx !== -1 ? itemIdx : userRuns.length;
        userRuns[idx] = userRun;
        return userRuns;
    };

    getUserRuns = (patientId: string): DB_UserRun[] | undefined => this.userRuns?.filter(r => r.patientId === patientId);

    deleteTodayRuns(condition: Clause_Where<DB_UserRun>) {
        XhrHttpModule
            .createRequest<ApiRemoveUserRuns>(HttpMethod.POST, "remove-user-runs")
            .setRelativeUrl("/v1/cohort/remove-user-runs")
            .setJsonBody(condition)
            .setOnError("Failed to remove user runs")
            .execute(async (): Promise<void> => {
                if (!this.userRuns)
                    return;

                const keys = _keys(condition);
                this.userRuns = this.userRuns.filter(r => {
                    for (const key of keys) {
                        if (typeof condition[key] !== "string")
                            continue;

                        if (r[key] !== condition[key])
                            return true;
                    }

                    return false;
                });
                dispatch_onUserRunsUpdated.dispatchUI();
            });
    }
}

export const PatientCohortModule = new PatientCohortModule_Class();
